import { DEFAULT_MASTER_PROMPT, generateFullPrompt, type LandscapingConfig } from '@paint-ai/shared';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Code2,
  Copy,
  Cpu,
  FileText,
  Layers,
  RotateCcw,
  Sliders,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { SegmentedControl, type SegmentOption } from '@/components/ui';
import type { ColorScheme } from '../../state/studio-reducer';
import { buildPayloadPreview } from './payload-preview';

type Tab = 'payload' | 'prompt' | 'directives';

const TABS: SegmentOption<Tab>[] = [
  {
    value: 'payload',
    id: 'payload-tab-json',
    label: 'API Payload (JSON)',
    icon: <Cpu className="w-3.5 h-3.5 text-sky-600" />,
  },
  {
    value: 'prompt',
    id: 'payload-tab-prompt',
    label: 'Synthesized Prompt',
    icon: <FileText className="w-3.5 h-3.5 text-emerald-600" />,
  },
  {
    value: 'directives',
    id: 'payload-tab-directives',
    label: 'Edit Directives',
    icon: <Sliders className="w-3.5 h-3.5 text-amber-600" />,
  },
];

const codeBlock =
  'w-full bg-slate-950 p-3.5 rounded-xl text-xs leading-relaxed font-mono whitespace-pre-wrap max-h-72 overflow-y-auto border border-slate-800 shadow-inner custom-scrollbar';

interface PromptInspectorProps {
  masterPrompt: string;
  onMasterPromptChange: (value: string) => void;
  colors: ColorScheme;
  landscaping: LandscapingConfig;
  model: string;
  aspectRatio: string;
  image: string | null;
}

/** Shows exactly what will be sent to Gemini and lets the user edit the directives template. */
export function PromptInspector({
  masterPrompt,
  onMasterPromptChange,
  colors,
  landscaping,
  model,
  aspectRatio,
  image,
}: PromptInspectorProps) {
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState<Tab>('payload');
  const [copied, setCopied] = useState(false);

  const prompt = useMemo(
    () => generateFullPrompt(colors.wall, colors.border, colors.highlight, masterPrompt, landscaping),
    [colors, masterPrompt, landscaping],
  );
  const payloadJson = useMemo(
    () => JSON.stringify(buildPayloadPreview({ model, image, prompt, aspectRatio }), null, 2),
    [model, image, prompt, aspectRatio],
  );

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    const text = tab === 'prompt' ? prompt : tab === 'directives' ? masterPrompt : payloadJson;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard access can be denied (e.g. insecure origin); nothing useful to show.
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl shadow-xs overflow-hidden"
      id="gemini-payload-inspector"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors text-left"
        id="prompt-editor-toggle"
      >
        <span className="flex items-center gap-2.5">
          <span className="p-1.5 rounded-lg bg-sky-50 text-sky-600 border border-sky-100">
            <Code2 className="w-4 h-4" />
          </span>
          <span>
            <span className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block">
                Gemini API Request Payload
              </span>
              <span className="text-[10px] font-semibold font-mono bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded border border-sky-200">
                {model}
              </span>
            </span>
            <span className="text-xs text-slate-500 block">
              Payload & synthesized master prompt dispatched to Gemini
            </span>
          </span>
        </span>
        <span className="flex items-center gap-2">
          <span className="hidden sm:inline-flex text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-100 px-2 py-1 rounded-md border border-slate-200">
            {aspectRatio} Ratio • 2 Parts
          </span>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </span>
      </button>

      {open && (
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <SegmentedControl label="Payload view" tone="light" options={TABS} value={tab} onChange={setTab} />

            <div className="flex items-center gap-2">
              {tab === 'directives' && (
                <button
                  type="button"
                  onClick={() => onMasterPromptChange(DEFAULT_MASTER_PROMPT)}
                  className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 font-semibold bg-white border border-slate-200 px-2.5 py-1 rounded-lg shadow-2xs transition-colors"
                  title="Reset prompt directives to default specification"
                  id="prompt-reset-btn"
                >
                  <RotateCcw className="w-3 h-3 text-slate-400" /> Reset
                </button>
              )}
              <button
                type="button"
                onClick={copy}
                className="text-xs text-slate-700 hover:text-slate-900 flex items-center gap-1 font-semibold bg-white border border-slate-200 hover:border-slate-300 px-2.5 py-1 rounded-lg shadow-2xs transition-colors"
                id="copy-payload-btn"
                title="Copy the current view"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-emerald-700">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5 text-slate-500" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {tab === 'payload' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[11px] text-slate-500 px-1">
                <span className="font-mono">POST /api/v1/makeovers → ai.models.generateContent()</span>
                <span className="font-mono text-slate-400">format: application/json</span>
              </div>
              <pre className={`${codeBlock} text-sky-200 selection:bg-sky-700`}>{payloadJson}</pre>
            </div>
          )}

          {tab === 'prompt' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[11px] text-slate-500 px-1">
                <span className="flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-emerald-500" />
                  Exact synthesized text passed in{' '}
                  <code className="font-mono text-slate-700">contents.parts[1].text</code>
                </span>
                <span className="font-mono text-slate-400">{prompt.length} chars</span>
              </div>
              <pre className={`${codeBlock} text-emerald-200/90 selection:bg-emerald-700`}>{prompt}</pre>
            </div>
          )}

          {tab === 'directives' && (
            <div className="space-y-2">
              <label htmlFor="master-prompt-textarea" className="block text-[11px] text-slate-500 px-1">
                Customize the underlying rendering directives template:
              </label>
              <textarea
                id="master-prompt-textarea"
                value={masterPrompt}
                onChange={(e) => onMasterPromptChange(e.target.value)}
                rows={8}
                className="w-full bg-slate-900 text-slate-200 p-3.5 rounded-xl text-xs leading-relaxed font-mono resize-none focus:outline-none border border-slate-800 focus:ring-2 focus:ring-sky-500 custom-scrollbar shadow-inner"
                placeholder="Enter custom directives..."
              />
              <p className="text-[11px] text-slate-500 flex items-center gap-1 font-medium">
                <Layers className="w-3.5 h-3.5 text-sky-600" />
                Variables like{' '}
                <code className="text-sky-700 font-bold bg-sky-50 px-1 py-0.5 rounded border border-sky-200">
                  [WALL_HEX]
                </code>{' '}
                and{' '}
                <code className="text-sky-700 font-bold bg-sky-50 px-1 py-0.5 rounded border border-sky-200">
                  [BORDER_HEX]
                </code>{' '}
                automatically populate in the payload.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
